import React, { memo } from 'react';
import { ActionButton } from '@adobe/react-spectrum';
import { LegendRiskLowIcon } from 'app/assets/legend/LegendRiskLowIcon';
import { LegendRiskModerateIcon } from 'app/assets/legend/LegendRiskModerateIcon';
import { LegendRiskHighIcon } from 'app/assets/legend/LegendRiskHighIcon';
import { LegendInPlanningIcon } from 'app/assets/legend/LegendInPlanningIcon';
import { normalizeRiskLevelToStored, RISK_LEVELS } from 'utils/constants';
import { CommonTooltip } from './CommonTooltip';
import styles from './RiskLevelReadOnly.module.scss';

interface RiskLevelReadOnlyProps {
  riskLevel?: string;
  noMargin?: boolean;
  testId?: string;
}

export const RiskLevelReadOnly: React.FC<RiskLevelReadOnlyProps> = memo(
  ({ riskLevel, noMargin = false, testId }) => {
    const normalizedRiskLevel =
      normalizeRiskLevelToStored(riskLevel) ?? riskLevel;

    const getStatusIcon = () => {
      const size = 16;
      if (normalizedRiskLevel === RISK_LEVELS.LOW) {
        return <LegendRiskLowIcon width={size} height={size} />;
      } else if (normalizedRiskLevel === RISK_LEVELS.MODERATE) {
        return <LegendRiskModerateIcon width={size} height={size} />;
      } else if (normalizedRiskLevel === RISK_LEVELS.HIGH) {
        return <LegendRiskHighIcon width={size} height={size} />;
      } else if (normalizedRiskLevel === RISK_LEVELS.IN_PLANNING) {
        return <LegendInPlanningIcon width={size} height={size} />;
      }
      return null;
    };

    const getRiskLevelText = (): React.ReactNode => {
      let riskText = 'Unknown';

      if (normalizedRiskLevel === RISK_LEVELS.LOW) {
        riskText = 'Low';
      } else if (normalizedRiskLevel === RISK_LEVELS.MODERATE) {
        riskText = 'Moderate';
      } else if (normalizedRiskLevel === RISK_LEVELS.HIGH) {
        riskText = 'High';
      } else if (normalizedRiskLevel === RISK_LEVELS.IN_PLANNING) {
        riskText = 'In Planning';
      }

      // For "In Planning", show without "Risk Level:" prefix
      if (normalizedRiskLevel === RISK_LEVELS.IN_PLANNING) {
        return <>{riskText}</>;
      }

      return (
        <>
          Risk Level: <span style={{ fontWeight: 'bold' }}>{riskText}</span>
        </>
      );
    };

    const displayableRiskLevels = new Set<string>([
      RISK_LEVELS.HIGH,
      RISK_LEVELS.MODERATE,
      RISK_LEVELS.LOW,
      RISK_LEVELS.IN_PLANNING
    ]);

    // Only show if risk level exists and is valid
    if (
      !normalizedRiskLevel ||
      !displayableRiskLevels.has(normalizedRiskLevel)
    ) {
      return null;
    }

    return (
      <div className={styles.riskLevelContainer}>
        <CommonTooltip tooltipContent={getRiskLevelText()}>
          <ActionButton
            UNSAFE_className={styles.riskLevelIcon}
            UNSAFE_style={noMargin ? { marginTop: 0 } : undefined}
            data-testid={testId || `milestone-grid-risk-level-${riskLevel}`}
          >
            {getStatusIcon()}
          </ActionButton>
        </CommonTooltip>
      </div>
    );
  }
);

RiskLevelReadOnly.displayName = 'RiskLevelReadOnly';
